import { Basemap } from "components/basemap/basemap";
import { Binary, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  getMaptilerRasterTileUrl,
  MAPTILER_STYLE_KEYS,
} from "@/components/basemap/styles";
import { useTheme } from "@/components/theme-provider";
import { Switch } from "@/components/ui/switch";
import { MapLayerMouseEvent, MapRef } from "react-map-gl/maplibre";
import { Link as RouterLink } from "react-router-dom";
import {
  BUILDING_AGE_COLORS,
  DATA_LAYERS,
  FACADE_MATERIAL_COLORS,
  FEATURE_INSPECTOR_CONFIG,
  FIRST_FLOOR_ELEVATION_COLORS,
  LAYER_CONFIG,
  PLANNING_ZONE_COLORS,
  PLANNING_ZONE_LEGEND,
  ROOF_CONDITION_COLORS,
} from "./configs";
import { DownloadModal } from "./DownloadModal";
import {
  CHATSWOOD_COORDINATES,
  createInitialControlState,
  createVectorTileSource,
  getLayerBase,
  getLayerHeight,
  SURFACE_COVER_CONFIG,
} from "./map-utils";
import {
  FeaturePropertyValue,
  Geometry,
  HighlightedFeature,
  LayerControlState,
  OrderedTable,
  Point,
  SelectedFeature,
  TableConfig,
} from "./types";

// Helper function to create facade material color expression
const getFacadeMaterialColorExpression = (defaultColor: string) => {
  const expression: any[] = ["case"];

  Object.entries(FACADE_MATERIAL_COLORS).forEach(([material, color]) => {
    expression.push(
      ["==", ["get", "building_facade_material___facade_material"], material],
      color,
    );
  });

  expression.push(defaultColor);
  return expression;
};

// Helper function to create roof condition color expression
const getRoofConditionColorExpression = (defaultColor: string) => {
  const expression: any[] = ["case"];

  Object.entries(ROOF_CONDITION_COLORS).forEach(([condition, color]) => {
    expression.push(
      ["==", ["get", "building_roof_condition___roof_condition"], condition],
      color,
    );
  });

  expression.push(defaultColor);
  return expression;
};

// Helper function to create building age color expression based on built_end_year
const getBuildingAgeColorExpression = (defaultColor: string) => {
  return [
    "case",
    [
      "all",
      ["!=", ["get", "building_age___built_end_year"], null],
      ["!=", ["get", "building_age___built_end_year"], "Null"],
      ["!=", ["get", "building_age___built_end_year"], ""],
    ],
    [
      "case",
      ["<", ["to-number", ["get", "building_age___built_end_year"]], 1950],
      BUILDING_AGE_COLORS["Before 1950"],
      ["<", ["to-number", ["get", "building_age___built_end_year"]], 1980],
      BUILDING_AGE_COLORS["1950-1979"],
      ["<", ["to-number", ["get", "building_age___built_end_year"]], 1991],
      BUILDING_AGE_COLORS["1980-1990"],
      ["<", ["to-number", ["get", "building_age___built_end_year"]], 2004],
      BUILDING_AGE_COLORS["1991-2003"],
      ["<", ["to-number", ["get", "building_age___built_end_year"]], 2017],
      BUILDING_AGE_COLORS["2004-2016"],
      BUILDING_AGE_COLORS["2017-2025"],
    ],
    "#A9A9A9", // Grey for buildings without age data
  ] as any;
};

// Helper function to create first floor elevation color expression
const getFirstFloorElevationColorExpression = (defaultColor: string) => {
  return [
    "case",
    [
      "all",
      [
        "!=",
        ["get", "building_first_floor_elevation___first_floor_elevation"],
        null,
      ],
      [
        "!=",
        ["get", "building_first_floor_elevation___first_floor_elevation"],
        "Null",
      ],
      [
        "!=",
        ["get", "building_first_floor_elevation___first_floor_elevation"],
        "",
      ],
    ],
    [
      "case",
      [
        "<",
        [
          "to-number",
          ["get", "building_first_floor_elevation___first_floor_elevation"],
        ],
        0.5,
      ],
      FIRST_FLOOR_ELEVATION_COLORS["0.0-0.5m"],
      [
        "<",
        [
          "to-number",
          ["get", "building_first_floor_elevation___first_floor_elevation"],
        ],
        1.0,
      ],
      FIRST_FLOOR_ELEVATION_COLORS["0.5-1.0m"],
      FIRST_FLOOR_ELEVATION_COLORS["1.0m+"],
    ],
    "#A9A9A9", // Grey for buildings without FFE data
  ] as any;
};

// Helper function to create roof condition 2025 buildings color expression
const getRoofCondition2025ColorExpression = (defaultColor: string) => {
  const expression: any[] = ["case"];

  Object.entries(ROOF_CONDITION_COLORS).forEach(([condition, color]) => {
    expression.push(["==", ["get", "condition"], condition], color);
  });

  expression.push(defaultColor);
  return expression;
};

// Helper function to create planning zone color expression
const getPlanningZoneColorExpression = (defaultColor: string) => {
  const expression: any[] = ["case"];

  Object.entries(PLANNING_ZONE_COLORS).forEach(([zoneCode, color]) => {
    expression.push(["==", ["get", "zone_code"], zoneCode], color);
  });

  expression.push(defaultColor);
  return expression;
};

const getFeatureInspectorConfig = (layerName: string) => {
  const layerConfigMap: Record<string, string> = {
    building_parts: "building",
    building: "building",
    address: "address",
    cadastre: "cadastre",
    property: "property",
    roads: "roads",
    trees: "trees",
    solar_heights: "solar",
    solar: "solar",
    "surface-cover": "surfaceCover",
    roof_condition_2025_discoloration: "roofCondition2025",
    roof_condition_2025_ponding: "roofCondition2025",
    roof_condition_2025_rust: "roofCondition2025",
    roof_condition_2025_tarps: "roofCondition2025",
  };

  const configKey = layerConfigMap[layerName] || "general";
  return FEATURE_INSPECTOR_CONFIG[
    configKey as keyof typeof FEATURE_INSPECTOR_CONFIG
  ];
};

const groupPropertiesByTable = (
  properties: Record<string, FeaturePropertyValue>,
  layerName?: string,
): Record<string, Record<string, FeaturePropertyValue>> => {
  const grouped: Record<string, Record<string, FeaturePropertyValue>> = {};
  const getDefaultTableName = (layerName?: string): string => {
    const defaultTableMap: Record<string, string> = {
      building_parts: "building",
      building: "building",
      address: "address",
      cadastre: "cadastre",
      property: "property",
      roads: "roads",
      trees: "trees",
      solar_heights: "solar_height",
      solar: "solar",
      "surface-cover": "surface_cover",
      roof_condition_2025_discoloration: "roof_condition_2025_discoloration",
      roof_condition_2025_ponding: "roof_condition_2025_ponding",
      roof_condition_2025_rust: "roof_condition_2025_rust",
      roof_condition_2025_tarps: "roof_condition_2025_tarps",
    };
    return layerName ? defaultTableMap[layerName] || "general" : "general";
  };

  const defaultTableName = getDefaultTableName(layerName);

  Object.entries(properties).forEach(([key, value]) => {
    if (key.includes("___")) {
      const [tableName, fieldName] = key.split("___");

      if (!grouped[tableName]) {
        grouped[tableName] = {};
      }
      grouped[tableName][fieldName] = value;
    } else {
      if (!grouped[defaultTableName]) {
        grouped[defaultTableName] = {};
      }
      grouped[defaultTableName][key] = value;
    }
  });

  return grouped;
};

const getOrderedTables = (
  groupedProperties: Record<string, Record<string, FeaturePropertyValue>>,
  layerName?: string,
): OrderedTable[] => {
  const config = layerName
    ? getFeatureInspectorConfig(layerName)
    : FEATURE_INSPECTOR_CONFIG.building;
  const tables = config.tables;
  const excludedTables = config.excludedTables;

  const filteredProperties = Object.fromEntries(
    Object.entries(groupedProperties).filter(
      ([tableName]) => !(excludedTables as string[]).includes(tableName),
    ),
  );

  const configMap = new Map(
    tables.map((table: TableConfig) => [table.key, table]),
  );

  const orderedTables: OrderedTable[] = [];
  for (const tableConfig of tables) {
    if (filteredProperties[tableConfig.key]) {
      if ("attributes" in tableConfig && tableConfig.attributes) {
        const orderedFields: Record<string, FeaturePropertyValue> = {};
        const availableFields = filteredProperties[tableConfig.key];

        for (const attrName of tableConfig.attributes) {
          if (availableFields.hasOwnProperty(attrName)) {
            orderedFields[attrName] = availableFields[attrName];
          }
        }

        orderedTables.push({
          key: tableConfig.key,
          displayName: tableConfig.displayName,
          fields: orderedFields,
          hasAttributes: true,
        });
      } else {
        orderedTables.push({
          key: tableConfig.key,
          displayName: tableConfig.displayName,
          fields: { ERROR: "MISSING ATTRIBUTES DEFINITION!" },
          hasAttributes: false,
        });
      }
    }
  }

  for (const [tableName, tableFields] of Object.entries(filteredProperties)) {
    if (!configMap.has(tableName)) {
      orderedTables.push({
        key: tableName,
        displayName: tableName.replace(/_/g, " "),
        fields: { ERROR: "MISSING ATTRIBUTES DEFINITION!" },
        hasAttributes: false,
      });
    }
  }

  return orderedTables;
};

export const NationalBuildingsChatswood = () => {
  const mapRef = useRef<MapRef>(null);
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const [coordinates] = useState(CHATSWOOD_COORDINATES);
  const [dynamicZoom, setDynamicZoom] = useState(15);
  const [layersEnabled, setLayersEnabled] = useState<Record<string, boolean>>(
    DATA_LAYERS.reduce(
      (acc, layer) => ({ ...acc, [layer.name]: layer.enabled }),
      {},
    ),
  );
  // Layer Optoons - Left Menu - Layer Selector
  const [layerControlState, setLayerControlState] = useState<LayerControlState>(
    createInitialControlState(),
  );

  const [selectedFeature, setSelectedFeature] =
    useState<SelectedFeature | null>(null);
  const [highlightedFeature, setHighlightedFeature] =
    useState<HighlightedFeature | null>(null);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [expandedAccordions, setExpandedAccordions] = useState<string[] | null>(
    null,
  );

  const onClick = async (event: MapLayerMouseEvent) => {
    const features = event.features;
    if (features && features.length > 0) {
      // Clear Feature Inspector immediately before processing new selection

      const feature = features[0];

      if (feature.layer?.id !== "cadastre-fill-layer") {
        setSelectedFeature(null);
      }

      let layerName = feature.layer?.id?.replace("-layer", "") || "Unknown";

      // Handle cadastre fill layer clicks (treat as cadastre clicks)
      if (feature.layer?.id === "cadastre-fill-layer") {
        layerName = "cadastre";
      }

      // Reset all layers to their default colors before highlighting new feature
      const map = mapRef.current?.getMap();
      if (map) {
        DATA_LAYERS.forEach((layer) => {
          const layerId = `${layer.name}-layer`;
          if (map.getLayer(layerId)) {
            if (layer.type === "circle") {
              map.setPaintProperty(layerId, "circle-color", layer.color);
            } else if (layer.type === "line") {
              // Check if cadastre layer is in planning mode
              let lineColor = layer.color;

              // Use planning zone colors for cadastre lines in planning mode
              if (
                layer.name === "cadastre" &&
                layerControlState["national-cadastre-radio"] === "planning"
              ) {
                lineColor = getPlanningZoneColorExpression(layer.color) as any;
              }

              map.setPaintProperty(layerId, "line-color", lineColor);

              // Also reset cadastre fill layer if in planning mode
              if (
                layer.name === "cadastre" &&
                layerControlState["national-cadastre-radio"] === "planning" &&
                map.getLayer("cadastre-fill-layer")
              ) {
                const fillColor = getPlanningZoneColorExpression(
                  layer.color,
                ) as any;
                map.setPaintProperty(
                  "cadastre-fill-layer",
                  "fill-color",
                  fillColor,
                );
                // Reset opacity to default 0.5 when clicking any feature
                map.setPaintProperty(
                  "cadastre-fill-layer",
                  "fill-opacity",
                  0.5,
                );
              }
            } else if (layer.type === "fill") {
              map.setPaintProperty(layerId, "fill-color", layer.color);
            } else if (layer.type === "extrude") {
              let fillExtrusionColor;

              if (layer.name === "trees") {
                fillExtrusionColor = [
                  "interpolate",
                  ["linear"],
                  ["get", "height"],
                  0,
                  "#5FBF7E",
                  40,
                  "#267D6C",
                ] as any;
              } else if (
                (layer.name === "building" ||
                  layer.name === "building_parts") &&
                layerControlState["national-buildings-radio"] === "solar"
              ) {
                fillExtrusionColor = [
                  "case",
                  [
                    "any",
                    ["==", ["get", "solar_flag"], "Yes"],
                    ["==", ["get", "building_solar___solar_flag"], "Yes"],
                  ],
                  "#EDC228",
                  layer.color,
                ] as any;
              } else if (
                (layer.name === "building" ||
                  layer.name === "building_parts") &&
                layerControlState["national-buildings-radio"] === "roof"
              ) {
                fillExtrusionColor = [
                  "case",
                  ["!=", ["get", "building_roof___roof_colour"], null],
                  ["get", "building_roof___roof_colour"],
                  ["!=", ["get", "roof_colour"], null],
                  ["get", "roof_colour"],
                  layer.color,
                ] as any;
              } else if (
                (layer.name === "building" ||
                  layer.name === "building_parts") &&
                layerControlState["national-buildings-radio"] === "age"
              ) {
                fillExtrusionColor = getBuildingAgeColorExpression(
                  layer.color,
                ) as any;
              } else if (
                (layer.name === "building" ||
                  layer.name === "building_parts") &&
                layerControlState["national-buildings-radio"] === "facade"
              ) {
                fillExtrusionColor = getFacadeMaterialColorExpression(
                  layer.color,
                ) as any;
              } else if (
                (layer.name === "building" ||
                  layer.name === "building_parts") &&
                layerControlState["national-buildings-radio"] === "elevation"
              ) {
                fillExtrusionColor = getFirstFloorElevationColorExpression(
                  layer.color,
                ) as any;
              } else if (
                (layer.name === "building" ||
                  layer.name === "building_parts") &&
                layerControlState["national-buildings-radio"] ===
                  "roof-condition"
              ) {
                fillExtrusionColor = getRoofConditionColorExpression(
                  layer.color,
                ) as any;
              } else {
                fillExtrusionColor = layer.color;
              }

              map.setPaintProperty(
                layerId,
                "fill-extrusion-color",
                fillExtrusionColor,
              );
            }
          }
        });
      }

      if (feature.layer?.id) {
        const featureIdentifier =
          feature.id ||
          (feature.geometry?.type === "Point"
            ? `${feature.geometry.coordinates[0]}_${feature.geometry.coordinates[1]}`
            : JSON.stringify(feature.properties).slice(0, 50));

        // For cadastre fill layer, use the line layer ID for highlighting
        const highlightLayerId =
          feature.layer.id === "cadastre-fill-layer"
            ? "cadastre-layer"
            : feature.layer.id;

        setHighlightedFeature({
          layerId: highlightLayerId,
          sourceId: `${layerName}-source`,
          sourceLayer: feature.sourceLayer || "",
          featureId: featureIdentifier,
        });
      } else {
        setHighlightedFeature(null);
      }

      if (layerName === "building_parts") {
        const buildingPid =
          feature.properties?.building_pid ||
          feature.properties?.["building_parts___building_pid"];

        if (buildingPid) {
          try {
            const map = mapRef.current?.getMap();
            if (map) {
              const buildingFeatures = map.querySourceFeatures(
                "building-source",
                {
                  sourceLayer: "denormalized_buildings",
                  filter: [
                    "any",
                    ["==", ["get", "building_pid"], buildingPid],
                    ["==", ["get", "building___building_pid"], buildingPid],
                  ] as any,
                },
              );

              if (buildingFeatures && buildingFeatures.length > 0) {
                setSelectedFeature({
                  layer: "building",
                  properties: buildingFeatures[0].properties,
                  geometry: buildingFeatures[0].geometry,
                });
              } else {
                setSelectedFeature({
                  layer: layerName,
                  properties: feature.properties,
                  geometry: feature.geometry,
                });
              }
            }
          } catch (error) {
            setSelectedFeature({
              layer: layerName,
              properties: feature.properties,
              geometry: feature.geometry,
            });
          }
        } else {
          setSelectedFeature({
            layer: layerName,
            properties: feature.properties,
            geometry: feature.geometry,
          });
        }
      } else {
        setSelectedFeature({
          layer: layerName,
          properties: feature.properties,
          geometry: feature.geometry,
        });
      }
    } else {
      setSelectedFeature(null);
      setHighlightedFeature(null);
    }
  };

  const handleZoom = (zoom: number): void => {
    setDynamicZoom(zoom);
    mapRef.current?.flyTo({ zoom: zoom, duration: 500 });
  };

  const resetCompass = () => {
    if (!mapRef.current) return;
    mapRef.current.easeTo({
      bearing: 0,
      pitch: 0,
    });
  };

  /**
   * Effect 1: Map Initialization & Basemap Change Handler
   * Runs when mapRef becomes available or when basemap selection changes
   *
   * Initializes all map data layers when the map is loaded:
   * - Adds surface cover raster layer (image overlay)
   * - Adds vector tile layers (buildings, roads, trees, cadastre, etc.)
   * - Applies conditional styling based on layerControlState (solar/roof modes)
   * - Creates road label and speed limit symbol layers
   *
   * When basemap changes, MapLibre reloads the style which removes all custom layers.
   * This effect re-adds them after the style change by listening to 'styledata' event.
   * Waits for map to be fully loaded before adding layers using 'load' and 'styledata' events.
   */

  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current.getMap();

    const addDataLayers = () => {
      try {
        // Add basemap layers first (at the bottom of the stack)
        const basemapSelection = layerControlState.basemap || "None";

        const geoscapeStyleKey = isDark
          ? MAPTILER_STYLE_KEYS.DARK
          : MAPTILER_STYLE_KEYS.LIGHT;

        // Tiles are null when the style URL is unset, so the basemap is skipped.
        const geoscapeTiles = getMaptilerRasterTileUrl(geoscapeStyleKey);
        if (geoscapeTiles && !map.getSource("geoscape-basemap-source")) {
          map.addSource("geoscape-basemap-source", {
            type: "raster",
            tiles: [geoscapeTiles],
            // MapTiler serves rasterised style tiles at 512px.
            tileSize: 512,
          });
        }
        if (
          map.getSource("geoscape-basemap-source") &&
          !map.getLayer("geoscape-basemap-layer")
        ) {
          map.addLayer({
            id: "geoscape-basemap-layer",
            type: "raster",
            source: "geoscape-basemap-source",
            layout: {
              visibility: basemapSelection === "Geoscape" ? "visible" : "none",
            },
          });
        }

        // Add Imagery basemap
        const imageryTiles = getMaptilerRasterTileUrl(
          MAPTILER_STYLE_KEYS.SATELLITE
        );
        if (imageryTiles && !map.getSource("imagery-basemap-source")) {
          map.addSource("imagery-basemap-source", {
            type: "raster",
            tiles: [imageryTiles],
            tileSize: 512,
          });
        }
        if (
          map.getSource("imagery-basemap-source") &&
          !map.getLayer("imagery-basemap-layer")
        ) {
          map.addLayer({
            id: "imagery-basemap-layer",
            type: "raster",
            source: "imagery-basemap-source",
            layout: {
              visibility:
                basemapSelection === "Satellite" ? "visible" : "none",
            },
          });
        }

        // Add Aerial Aerometrex 2025 basemap
        if (!map.getSource("satellite-2025-basemap-source")) {
          map.addSource("satellite-2025-basemap-source", {
            type: "raster",
            tiles: [
              "https://chatswood-demo-v2.s3.ap-southeast-2.amazonaws.com/chatswood-demo/aerial_2025_tiles/{z}/{x}/{y}.png",
            ],
            tileSize: 256,
          });
        }
        if (!map.getLayer("satellite-2025-basemap-layer")) {
          map.addLayer({
            id: "satellite-2025-basemap-layer",
            type: "raster",
            source: "satellite-2025-basemap-source",
            layout: {
              visibility:
                basemapSelection === "Aerial Aerometrex 2025"
                  ? "visible"
                  : "none",
            },
          });
        }

        const surfaceCoverLayer = DATA_LAYERS.find(
          (layer) => layer.name === "surface-cover",
        );

        if (!map.getSource("surface-cover-source")) {
          map.addSource("surface-cover-source", {
            type: "image",
            url: SURFACE_COVER_CONFIG.url,
            coordinates: SURFACE_COVER_CONFIG.coordinates,
          });
        }

        if (!map.getLayer("surface-cover-layer")) {
          map.addLayer({
            id: "surface-cover-layer",
            type: "raster",
            source: "surface-cover-source",
            paint: {
              "raster-opacity": 1,
            },
            layout: {
              visibility: layersEnabled["surface-cover"] ? "visible" : "none",
            },
          });
        }

        const vectorLayers = DATA_LAYERS.filter(
          (layer) => layer.type !== "raster",
        );

        for (const layer of vectorLayers) {
          if (!map.getSource(`${layer.name}-source`)) {
            map.addSource(
              `${layer.name}-source`,
              createVectorTileSource(layer),
            );
          }

          const isEnabled = layersEnabled[layer.name];

          if (layer.type === "circle" && !map.getLayer(`${layer.name}-layer`)) {
            map.addLayer({
              id: `${layer.name}-layer`,
              type: "circle",
              source: `${layer.name}-source`,
              "source-layer": layer.sourceLayer,
              paint: {
                "circle-radius": 4,
                "circle-color": layer.color,
                "circle-opacity": 1,
                "circle-stroke-width": 0,
                "circle-stroke-color": "#ffffff",
              },
              layout: {
                visibility: isEnabled ? "visible" : "none",
              },
            });
          } else if (
            layer.type === "line" &&
            !map.getLayer(`${layer.name}-layer`)
          ) {
            // Determine line color based on layer and control state
            let lineColor = layer.color;
            if (
              layer.name === "cadastre" &&
              layerControlState["national-cadastre-radio"] === "planning"
            ) {
              lineColor = getPlanningZoneColorExpression(layer.color) as any;
            }

            map.addLayer({
              id: `${layer.name}-layer`,
              type: "line",
              source: `${layer.name}-source`,
              "source-layer": layer.sourceLayer,
              paint: {
                "line-color": lineColor,
                "line-width": [
                  "interpolate",
                  ["exponential", 1.5],
                  ["zoom"],
                  14,
                  1,
                  18,
                  4,
                ] as any,
                "line-opacity": 1,
              },
              layout: {
                visibility: isEnabled ? "visible" : "none",
              },
            });
          } else if (
            layer.type === "fill" &&
            !map.getLayer(`${layer.name}-layer`)
          ) {
            // Determine fill opacity based on layer type
            const isRoofCondition2025 = layer.name.startsWith(
              "roof_condition_2025_",
            );
            const fillOpacity = isRoofCondition2025 ? 0.5 : 1;

            map.addLayer({
              id: `${layer.name}-layer`,
              type: "fill",
              source: `${layer.name}-source`,
              "source-layer": layer.sourceLayer,
              paint: {
                "fill-color": layer.color,
                "fill-opacity": fillOpacity,
                "fill-outline-color": layer.color,
              },
              layout: {
                visibility: isEnabled ? "visible" : "none",
              },
            });

            // Add separate line layer for roof condition 2025 with 90% opacity
            if (isRoofCondition2025) {
              map.addLayer({
                id: `${layer.name}-line-layer`,
                type: "line",
                source: `${layer.name}-source`,
                "source-layer": layer.sourceLayer,
                paint: {
                  "line-color": layer.color,
                  "line-width": 1,
                  "line-opacity": 0.9,
                },
                layout: {
                  visibility: isEnabled ? "visible" : "none",
                },
              });
            }
          } else if (
            layer.type === "extrude" &&
            !map.getLayer(`${layer.name}-layer`)
          ) {
            let fillExtrusionColor;

            if (layer.name === "trees") {
              fillExtrusionColor = [
                "interpolate",
                ["linear"],
                ["get", "height"],
                0,
                "#5FBF7E",
                40,
                "#267D6C",
              ] as any;
            } else if (
              (layer.name === "building" || layer.name === "building_parts") &&
              layerControlState["national-buildings-radio"] === "solar"
            ) {
              fillExtrusionColor = [
                "case",
                [
                  "any",
                  ["==", ["get", "solar_flag"], "Yes"],
                  ["==", ["get", "building_solar___solar_flag"], "Yes"],
                ],
                "#EDC228",
                layer.color,
              ] as any;
            } else if (
              (layer.name === "building" || layer.name === "building_parts") &&
              layerControlState["national-buildings-radio"] === "roof"
            ) {
              fillExtrusionColor = [
                "case",
                ["!=", ["get", "building_roof___roof_colour"], null],
                ["get", "building_roof___roof_colour"],
                ["!=", ["get", "roof_colour"], null],
                ["get", "roof_colour"],
                layer.color,
              ] as any;
            } else if (
              (layer.name === "building" || layer.name === "building_parts") &&
              layerControlState["national-buildings-radio"] === "age"
            ) {
              fillExtrusionColor = getBuildingAgeColorExpression(
                layer.color,
              ) as any;
            } else if (
              (layer.name === "building" || layer.name === "building_parts") &&
              layerControlState["national-buildings-radio"] === "facade"
            ) {
              fillExtrusionColor = getFacadeMaterialColorExpression(
                layer.color,
              ) as any;
            } else if (
              (layer.name === "building" || layer.name === "building_parts") &&
              layerControlState["national-buildings-radio"] === "elevation"
            ) {
              fillExtrusionColor = getFirstFloorElevationColorExpression(
                layer.color,
              ) as any;
            } else if (
              (layer.name === "building" || layer.name === "building_parts") &&
              layerControlState["national-buildings-radio"] === "roof-condition"
            ) {
              fillExtrusionColor = getRoofConditionColorExpression(
                layer.color,
              ) as any;
            } else {
              fillExtrusionColor = layer.color;
            }

            map.addLayer({
              id: `${layer.name}-layer`,
              type: "fill-extrusion",
              source: `${layer.name}-source`,
              "source-layer": layer.sourceLayer,
              paint: {
                "fill-extrusion-color": fillExtrusionColor,
                "fill-extrusion-height": getLayerHeight(layer) as any,
                "fill-extrusion-base": getLayerBase(layer) as any,
                "fill-extrusion-opacity": 1,
              },
              layout: {
                visibility: isEnabled ? "visible" : "none",
              },
            });
          }
        }

        const roadsLayer = DATA_LAYERS.find((layer) => layer.name === "roads");
        if (roadsLayer) {
          const buildingLayers = ["building-layer", "building_parts-layer"];
          let beforeLayer;
          for (const layerId of buildingLayers) {
            if (map.getLayer(layerId)) {
              beforeLayer = layerId;
              break;
            }
          }

          if (!map.getLayer("roads-labels")) {
            map.addLayer(
              {
                id: "roads-labels",
                type: "symbol",
                source: "roads-source",
                "source-layer": roadsLayer.sourceLayer,
                layout: {
                  "text-field": [
                    "concat",
                    ["get", "road_name_label"],
                    " ",
                    ["get", "road_type_label"],
                  ],
                  "text-size": 14,
                  "text-anchor": "center",
                  "symbol-placement": "line",
                  "text-rotation-alignment": "map",
                  "text-pitch-alignment": "viewport",
                  visibility:
                    layersEnabled.roads &&
                    layerControlState["national-roads-radio"] === "labels"
                      ? "visible"
                      : "none",
                },
                paint: {
                  "text-color": "#d3d3d3",
                  "text-halo-color": "#404040",
                  "text-halo-width": 2,
                },
              },
              beforeLayer,
            );
          }

          if (!map.getLayer("roads-speeds")) {
            map.addLayer(
              {
                id: "roads-speeds",
                type: "symbol",
                source: "roads-source",
                "source-layer": roadsLayer.sourceLayer,
                layout: {
                  "text-field": [
                    "case",
                    ["!=", ["get", "speed"], null],
                    ["concat", ["to-string", ["get", "speed"]], " km/h"],
                    "",
                  ],
                  "text-size": 14,
                  "text-anchor": "center",
                  "symbol-placement": "line",
                  "text-rotation-alignment": "map",
                  "text-pitch-alignment": "viewport",
                  visibility:
                    layersEnabled.roads &&
                    layerControlState["national-roads-radio"] === "speeds"
                      ? "visible"
                      : "none",
                },
                paint: {
                  "text-color": "#d3d3d3",
                  "text-halo-color": "#404040",
                  "text-halo-width": 2,
                },
              },
              beforeLayer,
            );
          }
        }
      } catch (error) {
        console.error("Error adding data layers:", error);
      }
    };

    const handleMapReady = () => {
      if (map.isStyleLoaded()) {
        addDataLayers();
      }
    };

    if (map.loaded() && map.isStyleLoaded()) {
      handleMapReady();
    } else {
      map.once("load", handleMapReady);
    }

    // A theme switch swaps the base style and drops every layer added above,
    // so re-add them each time a new style finishes loading.
    map.on("style.load", addDataLayers);

    return () => {
      map.off("load", handleMapReady);
      map.off("style.load", addDataLayers);
    };
  }, [mapRef.current, isDark]);

  /**
   * Effect 2: Basemap Visibility Toggle
   * Runs when basemap selection changes
   *
   * Updates basemap layer visibility without re-creating all layers
   */
  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current.getMap();
    const basemapSelection = layerControlState.basemap || "None";

    if (map.getLayer("geoscape-basemap-layer")) {
      map.setLayoutProperty(
        "geoscape-basemap-layer",
        "visibility",
        basemapSelection === "Geoscape" ? "visible" : "none",
      );
    }

    if (map.getLayer("imagery-basemap-layer")) {
      map.setLayoutProperty(
        "imagery-basemap-layer",
        "visibility",
        basemapSelection === "Satellite" ? "visible" : "none",
      );
    }

    if (map.getLayer("satellite-2025-basemap-layer")) {
      map.setLayoutProperty(
        "satellite-2025-basemap-layer",
        "visibility",
        basemapSelection === "Aerial Aerometrex 2025" ? "visible" : "none",
      );
    }
  }, [layerControlState.basemap]);

  /**
   * Effect 3: Layer Visibility Toggle
   * Runs when layersEnabled state changes (user toggles layer checkboxes)
   *
   * Updates the visibility of map layers without re-creating them:
   * - Iterates through layersEnabled object
   * - Sets each layer's visibility to "visible" or "none"
   * - Efficient layer on/off switching
   */
  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current.getMap();

    Object.entries(layersEnabled).forEach(([layerName, enabled]) => {
      // Skip roof condition 2025 layers - they're handled by Effect 5 with radio buttons
      if (layerName.startsWith("roof_condition_2025_")) {
        return;
      }

      const layerId = `${layerName}-layer`;
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(
          layerId,
          "visibility",
          enabled ? "visible" : "none",
        );
      }

      // Also toggle visibility for roof condition line layers
      const lineLayerId = `${layerName}-line-layer`;
      if (map.getLayer(lineLayerId)) {
        map.setLayoutProperty(
          lineLayerId,
          "visibility",
          enabled ? "visible" : "none",
        );
      }
    });
  }, [layersEnabled]);

  /**
   * Effect 4: Building Color Mode
   * Runs when the building radio selection changes (default/solar/roof)
   *
   * Updates fill-extrusion-color for building layers based on selected mode:
   * - "solar": Highlights buildings with solar panels in yellow (#EDC228)
   * - "roof": Colors buildings by their actual roof color from properties
   * - "default": Uses the standard layer color
   *
   * Applies to both "building" and "building_parts" layers
   */
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current.getMap();

    ["building", "building_parts"].forEach((layerName) => {
      const layer = DATA_LAYERS.find((l) => l.name === layerName);
      if (!layer || !map.getLayer(`${layerName}-layer`)) return;

      let fillExtrusionColor;
      if (layerControlState["national-buildings-radio"] === "solar") {
        fillExtrusionColor = [
          "case",
          [
            "any",
            ["==", ["get", "solar_flag"], "Yes"],
            ["==", ["get", "building_solar___solar_flag"], "Yes"],
          ],
          "#EDC228",
          layer.color,
        ] as any;
      } else if (layerControlState["national-buildings-radio"] === "roof") {
        fillExtrusionColor = [
          "case",
          ["!=", ["get", "building_roof___roof_colour"], null],
          ["get", "building_roof___roof_colour"],
          ["!=", ["get", "roof_colour"], null],
          ["get", "roof_colour"],
          layer.color,
        ] as any;
      } else if (layerControlState["national-buildings-radio"] === "age") {
        fillExtrusionColor = getBuildingAgeColorExpression(layer.color) as any;
      } else if (layerControlState["national-buildings-radio"] === "facade") {
        fillExtrusionColor = getFacadeMaterialColorExpression(
          layer.color,
        ) as any;
      } else if (
        layerControlState["national-buildings-radio"] === "elevation"
      ) {
        fillExtrusionColor = getFirstFloorElevationColorExpression(
          layer.color,
        ) as any;
      } else if (
        layerControlState["national-buildings-radio"] === "roof-condition"
      ) {
        fillExtrusionColor = getRoofConditionColorExpression(
          layer.color,
        ) as any;
      } else {
        fillExtrusionColor = layer.color;
      }

      map.setPaintProperty(
        `${layerName}-layer`,
        "fill-extrusion-color",
        fillExtrusionColor,
      );
    });
  }, [layerControlState["national-buildings-radio"]]);

  /**
   * Effect 5: Roof Condition 2025 Display Mode
   * Runs when roof condition 2025 radio selection changes or layer is toggled
   *
   * Controls which roof condition layer is visible:
   * - "discoloration": Shows discoloration areas
   * - "ponding": Shows ponding areas
   * - "rust": Shows rust areas
   * - "tarps": Shows tarp areas
   */
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current.getMap();

    const roofConditionLayers = [
      "roof_condition_2025_discoloration",
      "roof_condition_2025_ponding",
      "roof_condition_2025_rust",
      "roof_condition_2025_tarps",
    ];

    const selectedMode =
      layerControlState["roof-condition-2025-radio"] || "discoloration";

    // Check if any of the roof condition layers are enabled
    const isEnabled = roofConditionLayers.some(
      (layerName) => layersEnabled[layerName],
    );

    roofConditionLayers.forEach((layerName) => {
      const layerId = `${layerName}-layer`;
      const lineLayerId = `${layerName}-line-layer`;

      if (map.getLayer(layerId)) {
        let shouldShow = false;

        if (isEnabled) {
          if (
            selectedMode === "discoloration" &&
            layerName === "roof_condition_2025_discoloration"
          ) {
            shouldShow = true;
          } else if (
            selectedMode === "ponding" &&
            layerName === "roof_condition_2025_ponding"
          ) {
            shouldShow = true;
          } else if (
            selectedMode === "rust" &&
            layerName === "roof_condition_2025_rust"
          ) {
            shouldShow = true;
          } else if (
            selectedMode === "tarps" &&
            layerName === "roof_condition_2025_tarps"
          ) {
            shouldShow = true;
          }
        }

        map.setLayoutProperty(
          layerId,
          "visibility",
          shouldShow ? "visible" : "none",
        );

        // Also toggle the line layer
        if (map.getLayer(lineLayerId)) {
          map.setLayoutProperty(
            lineLayerId,
            "visibility",
            shouldShow ? "visible" : "none",
          );
        }
      }
    });
  }, [
    layerControlState["roof-condition-2025-radio"],
    layersEnabled["roof_condition_2025_discoloration"],
    layersEnabled["roof_condition_2025_ponding"],
    layersEnabled["roof_condition_2025_rust"],
    layersEnabled["roof_condition_2025_tarps"],
  ]);

  /**
   * Effect 6: Roads Display Mode
   * Runs when roads radio selection changes (labels/speeds) or roads layer is toggled
   *
   * Controls which road symbol layer is visible:
   * - "labels": Shows road names and types (e.g., "Main Street")
   * - "speeds": Shows speed limits (e.g., "60 km/h")
   *
   * Only one mode displays at a time, and only when roads layer is enabled
   */
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current.getMap();

    const roadLabelsLayer = "roads-labels";
    const roadSpeedsLayer = "roads-speeds";

    if (map.getLayer(roadLabelsLayer)) {
      const shouldShowLabels =
        layersEnabled.roads &&
        layerControlState["national-roads-radio"] === "labels";
      map.setLayoutProperty(
        roadLabelsLayer,
        "visibility",
        shouldShowLabels ? "visible" : "none",
      );
    }

    if (map.getLayer(roadSpeedsLayer)) {
      const shouldShowSpeeds =
        layersEnabled.roads &&
        layerControlState["national-roads-radio"] === "speeds";
      map.setLayoutProperty(
        roadSpeedsLayer,
        "visibility",
        shouldShowSpeeds ? "visible" : "none",
      );
    }
  }, [layerControlState["national-roads-radio"], layersEnabled.roads]);

  /**
   * Effect 7: Cadastre Planning Zone Color Mode
   * Runs when the cadastre radio selection changes (default/planning)
   *
   * Updates visualization for cadastre layer based on selected mode:
   * - "planning": Shows filled polygons colored by zone_code (R1, R2, C1, etc.)
   * - "default": Shows standard purple boundary lines only
   */
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current.getMap();

    const cadastreLayer = DATA_LAYERS.find((l) => l.name === "cadastre");
    if (!cadastreLayer || !map.getLayer("cadastre-layer")) return;

    const isPlanningMode =
      layerControlState["national-cadastre-radio"] === "planning";
    const isCadastreEnabled = layersEnabled.cadastre;

    // Add or update the fill layer for planning zones
    if (isPlanningMode) {
      const fillColor = getPlanningZoneColorExpression(
        cadastreLayer.color,
      ) as any;

      if (!map.getLayer("cadastre-fill-layer")) {
        // Add fill layer if it doesn't exist
        map.addLayer(
          {
            id: "cadastre-fill-layer",
            type: "fill",
            source: "cadastre-source",
            "source-layer": cadastreLayer.sourceLayer,
            paint: {
              "fill-color": fillColor,
              "fill-opacity": 0.6,
            },
            layout: {
              visibility: isCadastreEnabled ? "visible" : "none",
            },
          },
          "cadastre-layer", // Add before the line layer
        );
      } else {
        // Update existing fill layer
        map.setPaintProperty("cadastre-fill-layer", "fill-color", fillColor);
        map.setLayoutProperty(
          "cadastre-fill-layer",
          "visibility",
          isCadastreEnabled ? "visible" : "none",
        );
      }

      // Update line layer to use planning colors for boundaries (100% color match)
      map.setPaintProperty("cadastre-layer", "line-color", fillColor);
    } else {
      // Default mode - hide fill layer and use standard line color
      if (map.getLayer("cadastre-fill-layer")) {
        map.setLayoutProperty("cadastre-fill-layer", "visibility", "none");
      }
      // Reset line color to default purple
      map.setPaintProperty("cadastre-layer", "line-color", cadastreLayer.color);
    }
  }, [layerControlState["national-cadastre-radio"], layersEnabled.cadastre]);

  /**
   * Effect 8: Feature Highlighting
   * Runs when a feature is clicked/selected or building color mode changes
   *
   * Manages visual highlighting of selected features:
   * - Highlights clicked feature in orange (#FFA500)
   * - For buildings: highlights all parts with same building_pid
   * - For points: matches by coordinates
   * - For other features: matches by unique property (id/fid/objectid)
   * - Maintains current layer styling (solar/roof modes) for non-highlighted features
   * - Resets all layers to default colors when no feature is selected
   *
   * Uses Mapbox expressions to conditionally style features based on properties
   */
  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current.getMap();

    if (highlightedFeature && selectedFeature) {
      const layer = DATA_LAYERS.find(
        (l) => `${l.name}-layer` === highlightedFeature.layerId,
      );

      if (layer) {
        let currentLayerColoring;

        if (layer.name === "trees") {
          currentLayerColoring = [
            "interpolate",
            ["linear"],
            ["get", "height"],
            0,
            "#5FBF7E",
            40,
            "#267D6C",
          ] as any;
        } else if (
          (layer.name === "building" || layer.name === "building_parts") &&
          layerControlState["national-buildings-radio"] === "solar"
        ) {
          currentLayerColoring = [
            "case",
            [
              "any",
              ["==", ["get", "solar_flag"], "Yes"],
              ["==", ["get", "building_solar___solar_flag"], "Yes"],
            ],
            "#EDC228",
            layer.color,
          ] as any;
        } else if (
          (layer.name === "building" || layer.name === "building_parts") &&
          layerControlState["national-buildings-radio"] === "roof"
        ) {
          currentLayerColoring = [
            "case",
            ["!=", ["get", "building_roof___roof_colour"], null],
            ["get", "building_roof___roof_colour"],
            ["!=", ["get", "roof_colour"], null],
            ["get", "roof_colour"],
            layer.color,
          ] as any;
        } else if (
          (layer.name === "building" || layer.name === "building_parts") &&
          layerControlState["national-buildings-radio"] === "age"
        ) {
          currentLayerColoring = getBuildingAgeColorExpression(
            layer.color,
          ) as any;
        } else if (
          (layer.name === "building" || layer.name === "building_parts") &&
          layerControlState["national-buildings-radio"] === "facade"
        ) {
          currentLayerColoring = getFacadeMaterialColorExpression(
            layer.color,
          ) as any;
        } else if (
          (layer.name === "building" || layer.name === "building_parts") &&
          layerControlState["national-buildings-radio"] === "elevation"
        ) {
          currentLayerColoring = getFirstFloorElevationColorExpression(
            layer.color,
          ) as any;
        } else if (
          (layer.name === "building" || layer.name === "building_parts") &&
          layerControlState["national-buildings-radio"] === "roof-condition"
        ) {
          currentLayerColoring = getRoofConditionColorExpression(
            layer.color,
          ) as any;
        } else if (
          layer.name === "cadastre" &&
          layerControlState["national-cadastre-radio"] === "planning"
        ) {
          currentLayerColoring = getPlanningZoneColorExpression(
            layer.color,
          ) as any;
        } else {
          currentLayerColoring = layer.color;
        }

        let highlightExpression;

        if (layer.name === "building_parts" || layer.name === "building") {
          const buildingPid =
            selectedFeature.properties?.building_pid ||
            selectedFeature.properties?.["building_parts___building_pid"];

          if (buildingPid) {
            highlightExpression = [
              "case",
              [
                "any",
                ["==", ["get", "building_pid"], buildingPid],
                ["==", ["get", "building_parts___building_pid"], buildingPid],
              ],
              "#FFA500",
              currentLayerColoring,
            ] as any;
          } else {
            highlightExpression = currentLayerColoring;
          }
        } else if (layer.name === "cadastre") {
          const cadastrePid =
            selectedFeature.properties?.cadastre_polygon_pid ||
            selectedFeature.properties?.cadastre_pid;

          if (cadastrePid) {
            // In planning mode, use zone colors for non-selected lines
            // Otherwise use default purple
            const lineColor =
              layerControlState["national-cadastre-radio"] === "planning"
                ? currentLayerColoring
                : layer.color;

            highlightExpression = [
              "case",
              [
                "any",
                ["==", ["get", "cadastre_polygon_pid"], cadastrePid],
                ["==", ["get", "cadastre_pid"], cadastrePid],
              ],
              "#FFA500",
              lineColor, // Use zone colors in planning mode, default purple otherwise
            ] as any;
          } else {
            highlightExpression =
              layerControlState["national-cadastre-radio"] === "planning"
                ? currentLayerColoring
                : layer.color;
          }
        } else if ((selectedFeature.geometry as Geometry)?.type === "Point") {
          const coordinates = (selectedFeature.geometry as Point).coordinates;
          const [lng, lat] = coordinates;
          highlightExpression = [
            "case",
            ["all", ["==", ["get", "lng"], lng], ["==", ["get", "lat"], lat]],
            "#FFA500",
            currentLayerColoring,
          ] as any;
        } else {
          const props: Record<string, FeaturePropertyValue> =
            (selectedFeature.properties as Record<
              string,
              FeaturePropertyValue
            >) || {};
          const uniqueProperty =
            (typeof props.id === "string" ? props.id : null) ||
            (typeof props.fid === "string" ? props.fid : null) ||
            (typeof props.objectid === "string" ? props.objectid : null) ||
            Object.keys(props)[0];

          if (
            uniqueProperty &&
            typeof uniqueProperty === "string" &&
            props[uniqueProperty]
          ) {
            highlightExpression = [
              "case",
              ["==", ["get", uniqueProperty], props[uniqueProperty]],
              "#FFA500",
              currentLayerColoring,
            ] as any;
          } else {
            highlightExpression = currentLayerColoring;
          }
        }

        if (layer.type === "circle") {
          map.setPaintProperty(
            highlightedFeature.layerId,
            "circle-color",
            highlightExpression,
          );
        } else if (layer.type === "line") {
          map.setPaintProperty(
            highlightedFeature.layerId,
            "line-color",
            highlightExpression,
          );

          // Always handle cadastre fill layer in planning mode when any feature is selected
          if (
            layerControlState["national-cadastre-radio"] === "planning" &&
            map.getLayer("cadastre-fill-layer")
          ) {
            if (layer.name === "cadastre") {
              // Cadastre polygon is selected - highlight it
              const cadastrePid =
                selectedFeature.properties?.cadastre_polygon_pid ||
                selectedFeature.properties?.cadastre_pid;

              const fillHighlightExpression = [
                "case",
                [
                  "any",
                  ["==", ["get", "cadastre_polygon_pid"], cadastrePid],
                  ["==", ["get", "cadastre_pid"], cadastrePid],
                ],
                "#FFA500", // Orange for selected
                currentLayerColoring, // Planning zone colors for non-selected
              ] as any;

              // Also adjust opacity - make selected feature more visible
              const fillOpacityExpression = [
                "case",
                [
                  "any",
                  ["==", ["get", "cadastre_polygon_pid"], cadastrePid],
                  ["==", ["get", "cadastre_pid"], cadastrePid],
                ],
                0.3, // Higher opacity for selected (more visible orange)
                0.3, // Normal opacity for non-selected
              ] as any;

              map.setPaintProperty(
                "cadastre-fill-layer",
                "fill-color",
                fillHighlightExpression,
              );
              map.setPaintProperty(
                "cadastre-fill-layer",
                "fill-opacity",
                fillOpacityExpression,
              );
            } else {
              // Non-cadastre feature is selected (e.g., building) - reset cadastre to normal
              const cadastreLayer = DATA_LAYERS.find(
                (l) => l.name === "cadastre",
              );
              if (cadastreLayer) {
                const cadastreColors = getPlanningZoneColorExpression(
                  cadastreLayer.color,
                ) as any;
                map.setPaintProperty(
                  "cadastre-fill-layer",
                  "fill-color",
                  cadastreColors,
                );
                map.setPaintProperty(
                  "cadastre-fill-layer",
                  "fill-opacity",
                  0.3,
                );
              }
            }
          }
        } else if (layer.type === "fill") {
          map.setPaintProperty(
            highlightedFeature.layerId,
            "fill-color",
            highlightExpression,
          );
        } else if (layer.type === "extrude") {
          map.setPaintProperty(
            highlightedFeature.layerId,
            "fill-extrusion-color",
            highlightExpression,
          );
        }
      }
    } else {
      DATA_LAYERS.forEach((layer) => {
        const layerId = `${layer.name}-layer`;
        if (map.getLayer(layerId)) {
          if (layer.type === "circle") {
            map.setPaintProperty(layerId, "circle-color", layer.color);
          } else if (layer.type === "line") {
            // Check if cadastre layer is in planning mode
            let lineColor = layer.color;

            // Use planning zone colors for cadastre lines in planning mode
            if (
              layer.name === "cadastre" &&
              layerControlState["national-cadastre-radio"] === "planning"
            ) {
              lineColor = getPlanningZoneColorExpression(layer.color) as any;
            }

            map.setPaintProperty(layerId, "line-color", lineColor);

            // Reset cadastre fill layer opacity when no feature is selected
            if (
              layer.name === "cadastre" &&
              layerControlState["national-cadastre-radio"] === "planning" &&
              map.getLayer("cadastre-fill-layer")
            ) {
              map.setPaintProperty("cadastre-fill-layer", "fill-opacity", 0.3);
            }
          } else if (layer.type === "fill") {
            map.setPaintProperty(layerId, "fill-color", layer.color);
          } else if (layer.type === "extrude") {
            let fillExtrusionColor;

            if (layer.name === "trees") {
              fillExtrusionColor = [
                "interpolate",
                ["linear"],
                ["get", "height"],
                0,
                "#5FBF7E",
                40,
                "#267D6C",
              ] as any;
            } else if (
              (layer.name === "building" || layer.name === "building_parts") &&
              layerControlState["national-buildings-radio"] === "solar"
            ) {
              fillExtrusionColor = [
                "case",
                [
                  "any",
                  ["==", ["get", "solar_flag"], "Yes"],
                  ["==", ["get", "building_solar___solar_flag"], "Yes"],
                ],
                "#EDC228",
                layer.color,
              ] as any;
            } else if (
              (layer.name === "building" || layer.name === "building_parts") &&
              layerControlState["national-buildings-radio"] === "roof"
            ) {
              fillExtrusionColor = [
                "case",
                ["!=", ["get", "building_roof___roof_colour"], null],
                ["get", "building_roof___roof_colour"],
                ["!=", ["get", "roof_colour"], null],
                ["get", "roof_colour"],
                layer.color,
              ] as any;
            } else if (
              (layer.name === "building" || layer.name === "building_parts") &&
              layerControlState["national-buildings-radio"] === "age"
            ) {
              fillExtrusionColor = getBuildingAgeColorExpression(
                layer.color,
              ) as any;
            } else if (
              (layer.name === "building" || layer.name === "building_parts") &&
              layerControlState["national-buildings-radio"] === "facade"
            ) {
              fillExtrusionColor = getFacadeMaterialColorExpression(
                layer.color,
              ) as any;
            } else if (
              (layer.name === "building" || layer.name === "building_parts") &&
              layerControlState["national-buildings-radio"] === "elevation"
            ) {
              fillExtrusionColor = getFirstFloorElevationColorExpression(
                layer.color,
              ) as any;
            } else if (
              (layer.name === "building" || layer.name === "building_parts") &&
              layerControlState["national-buildings-radio"] === "roof-condition"
            ) {
              fillExtrusionColor = getRoofConditionColorExpression(
                layer.color,
              ) as any;
            } else {
              fillExtrusionColor = layer.color;
            }

            map.setPaintProperty(
              layerId,
              "fill-extrusion-color",
              fillExtrusionColor,
            );
          }
        }
      });
    }
  }, [
    highlightedFeature,
    selectedFeature,
    layerControlState["national-buildings-radio"],
    layerControlState["national-cadastre-radio"],
  ]);

  const toggleLayer = (layerName: string) => {
    setLayersEnabled((prev) => ({
      ...prev,
      [layerName]: !prev[layerName],
    }));
  };

  const handleLayerControlChange = (
    controlId: string,
    value: boolean | string,
  ) => {
    setLayerControlState((prev) => ({
      ...prev,
      [controlId]: value,
    }));

    const config = LAYER_CONFIG.find((c) => c.id === controlId);
    if (config?.mapLayers && typeof value === "boolean") {
      config.mapLayers.forEach((layerName) => {
        setLayersEnabled((prev) => ({
          ...prev,
          [layerName]: value,
        }));
      });
    }
  };

  const handleRadioChange = (controlId: string, value: string) => {
    setLayerControlState((prev) => ({
      ...prev,
      [`${controlId}-radio`]: value,
    }));
  };

  const handleButtonClick = (controlId: string) => {
    if (controlId === "download-sample") {
      setDownloadModalOpen(true);
    }
  };

  const handleDownload = (presignedUrl: string) => {
    const cleanUrl = presignedUrl.trim();

    if (cleanUrl.startsWith("http://") || cleanUrl.startsWith("https://")) {
      // Direct assignment to window.location.href for file downloads
      window.location.assign(cleanUrl);
    } else {
      console.error("Invalid URL format:", cleanUrl);
    }

    setDownloadModalOpen(false);
  };

  const handleModalClose = () => {
    setDownloadModalOpen(false);
  };

  const handleAccordionValueChange = (value: string[]) => {
    setExpandedAccordions(value);
  };

  return (
    <>
      <Basemap
        // @ts-ignore
        mapRef={mapRef}
        attributionControl={true}
        latitude={coordinates.latitude}
        longitude={coordinates.longitude}
        zoom={dynamicZoom}
        pitch={60}
        onZoom={(zoom) => setDynamicZoom(zoom)}
        onClick={onClick}
        interactiveLayerIds={[
          ...DATA_LAYERS.map((layer) => `${layer.name}-layer`),
          "cadastre-fill-layer",
        ]}
        minZoom={14}
        style={{
          width: "100%",
          height: "100%",
          position: "absolute",
          left: 0,
          top: 0,
          backgroundColor: isDark ? "#1a1a1a" : "#f5f5f5",
        }}
        mapStyle={"BLANK"}
        boundingBox={[150.5, -34.5, 152.0, -33.5]}
      >
        <div className="absolute bottom-4 left-4 top-20 w-65 group-has-data-[collapsible=icon]/sidebar-wrapper:top-16">
          <Card className="h-full gap-0 bg-card/95 py-2">
            <CardContent className="flex h-full flex-col px-2">
              <div className="mb-4 flex-1 overflow-y-auto pr-2">
                {LAYER_CONFIG.filter(
                  (config) => config.type === "checkbox",
                ).map((config) => (
                  <div key={config.id} className="mb-3">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={config.id}
                        checked={Boolean(layerControlState[config.id])}
                        onCheckedChange={(checked) =>
                          handleLayerControlChange(config.id, checked === true)
                        }
                      />
                      <Label htmlFor={config.id} className="text-sm font-bold">
                        {config.label}
                      </Label>
                    </div>

                    {config.radioOptions && layerControlState[config.id] && (
                      <div className="ml-4 mt-2">
                        <RadioGroup
                          value={
                            (layerControlState[
                              `${config.id}-radio`
                            ] as string) || "default"
                          }
                          onValueChange={(value) =>
                            handleRadioChange(config.id, value)
                          }
                          className="gap-1"
                        >
                          {config.radioOptions.map((option) => (
                            <div
                              key={option.value}
                              className="flex items-center gap-2"
                            >
                              <RadioGroupItem
                                id={`${config.id}-${option.value}`}
                                value={option.value}
                              />
                              <Label
                                htmlFor={`${config.id}-${option.value}`}
                                className="text-xs text-muted-foreground font-normal"
                              >
                                {option.label}
                              </Label>
                            </div>
                          ))}
                        </RadioGroup>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex flex-col gap-2 pt-2">
                {LAYER_CONFIG.filter(
                  (config) => config.type !== "checkbox",
                ).map((config) => (
                  <div key={config.id}>
                    {config.type === "switch" && (
                      <div className="flex items-center gap-2">
                        <Switch
                          id={config.id}
                          checked={Boolean(layerControlState[config.id])}
                          onCheckedChange={(checked) =>
                            handleLayerControlChange(config.id, checked)
                          }
                        />
                        <Label
                          htmlFor={config.id}
                          className="text-sm font-bold"
                        >
                          {config.label}
                        </Label>
                      </div>
                    )}

                    {config.type === "selector" && (
                      <div className="space-y-1">
                        <Label className="text-xs">{config.label}</Label>
                        <Select
                          value={
                            (layerControlState[config.id] as string) ||
                            config.options?.[0]
                          }
                          onValueChange={(value) =>
                            handleLayerControlChange(config.id, value)
                          }
                        >
                          <SelectTrigger className="text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent
                            position="popper"
                            className="z-1001 max-h-96"
                          >
                            {config.options?.map((option) => (
                              <SelectItem
                                key={option}
                                value={option}
                                className="text-xs"
                              >
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {config.type === "links" && config.links && (
                      <Accordion
                        type="single"
                        collapsible
                        defaultValue={config.id}
                        className="border-0"
                      >
                        <AccordionItem value={config.id} className="border-0">
                          <AccordionTrigger className="border-b p-0 pb-1 text-base font-medium">
                            {config.label}
                          </AccordionTrigger>
                          <AccordionContent className="pt-1 pb-1">
                            <ul className="m-0 flex flex-col gap-1 pl-2">
                              {config.links.map((item, index) => (
                                <li key={index}>
                                  <RouterLink
                                    target="_blank"
                                    className="text-primary no-underline"
                                    to={{ pathname: item.href }}
                                  >
                                    {item.label}
                                  </RouterLink>
                                </li>
                              ))}
                            </ul>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    )}

                    {config.type === "button" && (
                      <Button
                        className="w-full"
                        onClick={() => handleButtonClick(config.id)}
                      >
                        {config.label}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Legends Container */}
        <div className="absolute bottom-4 left-73 flex flex-row flex-wrap gap-4">
          {/* Facade Material Legend */}
          {layerControlState["national-buildings"] &&
            layerControlState["national-buildings-radio"] === "facade" && (
              <Card className="min-w-50 bg-card/95">
                <CardContent>
                  <h6 className="mb-2 text-sm font-bold">Facade Material</h6>
                  {Object.entries(FACADE_MATERIAL_COLORS).map(
                    ([material, color]) => (
                      <div key={material} className="mb-1 flex items-center">
                        <div
                          className="mr-2 size-5 border border-border/20"
                          style={{ backgroundColor: color }}
                        />
                        <span className="text-xs">{material}</span>
                      </div>
                    ),
                  )}
                </CardContent>
              </Card>
            )}

          {/* Roof Condition Legend */}
          {layerControlState["national-buildings"] &&
            layerControlState["national-buildings-radio"] ===
              "roof-condition" && (
              <Card className="min-w-50 bg-card/95">
                <CardContent>
                  <h6 className="mb-2 text-sm font-bold">Roof Condition</h6>
                  {Object.entries(ROOF_CONDITION_COLORS).map(
                    ([condition, color]) => (
                      <div key={condition} className="mb-1 flex items-center">
                        <div
                          className="mr-2 size-5 border border-border/20"
                          style={{ backgroundColor: color }}
                        />
                        <span className="text-xs capitalize">{condition}</span>
                      </div>
                    ),
                  )}
                </CardContent>
              </Card>
            )}

          {/* Building Age Legend */}
          {layerControlState["national-buildings"] &&
            layerControlState["national-buildings-radio"] === "age" && (
              <Card className="min-w-50 bg-card/95">
                <CardContent>
                  <h6 className="mb-2 text-sm font-bold">Building Age</h6>
                  {Object.entries(BUILDING_AGE_COLORS).map(
                    ([ageRange, color]) => (
                      <div key={ageRange} className="mb-1 flex items-center">
                        <div
                          className="mr-2 size-5 border border-border/20"
                          style={{ backgroundColor: color }}
                        />
                        <span className="text-xs">{ageRange}</span>
                      </div>
                    ),
                  )}
                </CardContent>
              </Card>
            )}

          {/* First Floor Elevation Legend */}
          {layerControlState["national-buildings"] &&
            layerControlState["national-buildings-radio"] === "elevation" && (
              <Card className="min-w-50 bg-card/95">
                <CardContent>
                  <h6 className="mb-2 text-sm font-bold">
                    First Floor Elevation
                  </h6>
                  {Object.entries(FIRST_FLOOR_ELEVATION_COLORS).map(
                    ([range, color]) => (
                      <div key={range} className="mb-1 flex items-center">
                        <div
                          className="mr-2 size-5 border border-border/20"
                          style={{ backgroundColor: color }}
                        />
                        <span className="text-xs">{range}</span>
                      </div>
                    ),
                  )}
                </CardContent>
              </Card>
            )}

          {/* Planning Zone Legend */}
          {layerControlState["national-cadastre"] &&
            layerControlState["national-cadastre-radio"] === "planning" && (
              <Card className="w-63 bg-card/95">
                <CardContent>
                  <h6 className="mb-2 text-sm font-bold">Planning Zones</h6>
                  {PLANNING_ZONE_LEGEND.map((category) => (
                    <div key={category.category} className="mb-2">
                      <p className="mb-1 text-[11px] font-bold uppercase text-muted-foreground">
                        {category.category}
                      </p>
                      {category.zones.map((zone) => (
                        <div
                          key={zone.code}
                          className="mb-0.5 ml-2 flex items-center"
                        >
                          <div
                            className="mr-1.5 size-4 shrink-0 border border-border/20"
                            style={{ backgroundColor: zone.color }}
                          />
                          <span className="text-[11px] leading-tight">
                            {zone.code} - {zone.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
        </div>

        {/* Feature Attributes Panel */}
        {selectedFeature && (
          <div className="absolute bottom-4 right-4 top-20 w-75 overflow-auto group-has-data-[collapsible=icon]/sidebar-wrapper:top-16">
            <Card className="flex h-full flex-col overflow-hidden bg-card/95">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Binary className="size-10" />
                  Feature Inspector
                </CardTitle>
                <CardAction>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Close feature inspector"
                    onClick={() => setSelectedFeature(null)}
                  >
                    <X className="size-4" />
                  </Button>
                </CardAction>
              </CardHeader>
              <CardContent className="flex flex-1 flex-col overflow-auto">
                <div className="flex-1 overflow-auto">
                  {selectedFeature.properties &&
                    (() => {
                      const groupedProperties = groupPropertiesByTable(
                        selectedFeature.properties as Record<
                          string,
                          FeaturePropertyValue
                        >,
                        selectedFeature.layer,
                      );
                      const orderedTables = getOrderedTables(
                        groupedProperties,
                        selectedFeature.layer,
                      );

                      const expandedKeys =
                        expandedAccordions === null
                          ? orderedTables
                              .slice(0, 1)
                              .map(
                                (table) => `${table.key}-${table.displayName}`,
                              )
                          : expandedAccordions;

                      return (
                        <Accordion
                          type="multiple"
                          value={expandedKeys}
                          onValueChange={handleAccordionValueChange}
                        >
                          {orderedTables.map((table) => {
                            const accordionKey = `${table.key}-${table.displayName}`;
                            return (
                              <AccordionItem
                                key={accordionKey}
                                value={accordionKey}
                              >
                                <AccordionTrigger>
                                  {table.displayName}
                                </AccordionTrigger>
                                <AccordionContent className="flex flex-col h-fit">
                                  {Object.entries(table.fields).map(
                                    ([fieldName, value]) => (
                                      <div
                                        key={fieldName}
                                        className="flex justify-between items-center"
                                      >
                                        <span className="text-xs text-muted-foreground">
                                          {fieldName}
                                        </span>
                                        <span className="text-sm">
                                          {(() => {
                                            if (
                                              value === null ||
                                              value === undefined ||
                                              value === ""
                                            )
                                              return "-";
                                            if (
                                              typeof value === "number" &&
                                              !Number.isInteger(value)
                                            ) {
                                              if (
                                                fieldName.includes(
                                                  "longitude",
                                                ) ||
                                                fieldName.includes("latitude")
                                              ) {
                                                return String(value);
                                              }
                                              return value.toFixed(2);
                                            }
                                            return String(value);
                                          })()}
                                        </span>
                                      </div>
                                    ),
                                  )}
                                </AccordionContent>
                              </AccordionItem>
                            );
                          })}
                        </Accordion>
                      );
                    })()}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </Basemap>

      <DownloadModal
        open={downloadModalOpen}
        onClose={handleModalClose}
        onDownload={handleDownload}
      />
    </>
  );
};
